import React, { useState, useEffect } from 'react';
import { X, TrendingDown, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../App';

/**
 * PriceReductionModal
 * Modal for reducing the price of a listing.
 * Validates that new price is lower than current price.
 */
const PriceReductionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  currentPrice, 
  originalPrice,
  currency = 'USD',
  isLoading = false 
}) => {
  const { t, language } = useLanguage();
  const [newPrice, setNewPrice] = useState('');
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewPrice('');
      setError('');
    }
  }, [isOpen]);

  // The price to compare against (original if exists, otherwise current)
  const comparePrice = originalPrice || currentPrice;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const priceValue = parseFloat(newPrice);
    
    if (isNaN(priceValue) || priceValue <= 0) {
      setError(language === 'nl' ? 'Voer een geldige prijs in' : 'Enter a valid price');
      return;
    }
    
    if (priceValue >= currentPrice) {
      setError(
        language === 'nl' 
          ? `Nieuwe prijs moet lager zijn dan ${currency} ${currentPrice.toLocaleString()}`
          : `New price must be lower than ${currency} ${currentPrice.toLocaleString()}`
      );
      return;
    }
    
    onConfirm(priceValue);
  };

  const formatPrice = (price) => {
    return price?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <TrendingDown className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                {language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {language === 'nl' ? 'Stel een nieuwe lagere prijs in' : 'Set a new lower price'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Price Display */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {language === 'nl' ? 'Huidige prijs' : 'Current price'}
              </span>
              <span className="font-semibold text-foreground">
                {currency} {formatPrice(currentPrice)}
              </span>
            </div>
            {originalPrice && originalPrice !== currentPrice && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {language === 'nl' ? 'Originele prijs' : 'Original price'}
                </span>
                <span className="text-sm text-muted-foreground line-through">
                  {currency} {formatPrice(originalPrice)}
                </span>
              </div>
            )}
          </div>
          
          {/* New Price Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {language === 'nl' ? 'Nieuwe prijs' : 'New price'}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                {currency}
              </span>
              <input
                type="number"
                value={newPrice}
                onChange={(e) => {
                  setNewPrice(e.target.value);
                  setError('');
                }}
                placeholder={language === 'nl' ? 'Voer nieuwe prijs in...' : 'Enter new price...'}
                className="w-full pl-16 pr-4 py-3 bg-secondary/50 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                min="0"
                step="0.01"
                autoFocus
                data-testid="price-reduction-input"
              />
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 px-4 py-3 rounded-xl">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Info Text */}
          <p className="text-xs text-muted-foreground">
            {language === 'nl' 
              ? 'De originele prijs wordt doorgestreept weergegeven. Gebruikers die deze advertentie hebben opgeslagen krijgen een melding.'
              : 'The original price will be shown crossed out. Users who saved this listing will be notified.'}
          </p>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-secondary text-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors"
              disabled={isLoading}
            >
              {language === 'nl' ? 'Annuleren' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isLoading || !newPrice}
              className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="confirm-price-reduction"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {language === 'nl' ? 'Bezig...' : 'Saving...'}
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4" />
                  {language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceReductionModal;
